<template>
    <rents-get
        :page="page"
        :archived="archived"
        :include="[
            'tenancy(include:property(include:address))',
        ]"
        :tenancy="$route.query.tenancy"
        v-slot="{
            data: rents,
            meta,
            error,
            is_loading,
            has_loaded,
            get
        }"
    >
        <div v-if="!has_loaded || error">
            <base-loading
                :error="error"
                :retry="get"
                class="py-6"
            />

            <error-modal
                v-if="error"
                :error="error"
            />
        </div>

        <div v-else>
            <base-hint
                v-if="$gate.role('landlord')"
                name="landlords-rents-index"
            >
                <template slot="heading">
                    Rents
                </template>

                <p>
                    Rents are listed in order of importance, but most of the time you'll see due then upcoming rents.
                </p>
            </base-hint>

            <base-hint
                v-if="$gate.role('tenant')"
                name="tenants-rents-index"
            >
                <template slot="heading">
                    Rents
                </template>

                <p class="mb-3">
                    Rents are listed in order of importance, but most of the time you'll see due then upcoming rents.
                </p>

                <p>
                    Remember, with Rentora you can pay your rent by credit or debit card - with the added flexibility of making smaller contributions as often as you like - even to future rents.
                </p>
            </base-hint>

            <div class="mb-6 bg-white rounded shadow">
                <header class="flex items-center px-4 py-3 sm:px-6 md:py-3">
                    <div class="flex-grow">
                        <h1 class="text-lg font-normal">Rents</h1>
                    </div>
                    <div class="w-6 h-6 mr-3">
                        <base-svg
                            v-show="is_loading"
                            name="icon-loading"
                            class="w-full h-full fill-current text-grey"
                        />
                    </div>

                    <base-dropdown
                        placement="bottom-end"
                    >
                        <button
                            slot="trigger"
                            slot-scope="{ is_open, toggle }"
                            :class="{ 'is-active': is_open }"
                            class="btn btn-with-icon"
                            type="button"
                            @click.prevent="toggle"
                        >
                            <div class="flex items-center w-full h-full">
                                <base-svg
                                    name="icon-funnel"
                                    class="w-6 h-6 md:mr-2"
                                />
                                <span class="hidden md:inline">Filter</span>
                            </div>
                        </button>
                        <div
                            slot="menu"
                            class="w-64 p-3 text-white rounded shadow-lg bg-grey-darker mt-3px"
                        >
                            <label class="flex items-center leading-none cursor-pointer">
                                <input-checkbox
                                    v-model="archived"
                                    class="mr-3"
                                />
                                <span>Archived</span>
                            </label>
                        </div>
                    </base-dropdown>
                </header>

                <template v-if="meta.count > 0">
                    <rents-list :rents="rents">
                        <template
                            slot="secondary"
                            slot-scope="{ rent }"
                        >
                            <truncate-hack>
                                {{ $gate.role('landlord') ? rent.tenancy.property.name : rent.tenancy.property.address.name }}
                            </truncate-hack>
                        </template>
                    </rents-list>

                    <footer class="px-4 text-sm font-semibold rounded-b bg-grey-bg sm:px-6 text-grey-darker">
                        <base-pagination
                            v-model="page"
                            :per-page="meta.per_page"
                            :from="meta.from"
                            :to="meta.to"
                            :last-page="meta.last_page"
                            :count="meta.count"
                            :total="meta.total"
                            :is-disabled="is_loading"
                        />
                    </footer>
                </template>

                <div
                    v-else
                    class="py-16 border-t"
                >
                    <div class="max-w-sm px-6 mx-auto text-center">
                        <h2 class="mb-2 text-xl font-semibold">
                            No rents
                        </h2>

                        <div class="text-base text-grey-darker">
                            <template v-if="meta.gross > 0">
                                <p>There's nothing here!</p>

                                <router-link
                                    :to="{
                                        name: $router.fix('dashboard.*.rents.index')
                                    }"
                                    class="text-blue"
                                >
                                    Reset filters &amp; go to page 1
                                </router-link>
                            </template>

                            <template v-else>
                                <p>There aren't any rents yet.</p>

                                <p
                                    v-if="$gate.role('landlord')"
                                    class="mt-6"
                                >
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.tenancies.create')
                                        }"
                                        class="mx-auto btn btn-blue btn-with-icon"
                                    >
                                        <div class="flex items-center w-full h-full">
                                            <base-svg
                                                    name="icon-add-circle"
                                                    class="w-6 h-6 mr-2"
                                            />

                                            New Tenancy
                                        </div>
                                    </router-link>
                                </p>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </rents-get>
</template>

<script>
    import PaginationMixin from '../../../../mixins/pagination'
    import RentsGet from '../../../../components/rents-get'
    import RentsList from '../../../../components/rents-list'
    import TruncateHack from '../../../../components/truncate-hack'

    export default {
        mixins: [
            PaginationMixin
        ],
        components: {
            RentsGet,
            RentsList,
            TruncateHack
        }
    }
</script>
